import { useState, useCallback, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
    ImageIcon,
    Trash2,
    Upload,
    Loader2,
    ExternalLink,
    Info,
    Calendar,
    HardDrive,
    ChevronDown
} from 'lucide-react'
import { useMedia, MediaFile } from '@/services/use-media'
import { FilePreview, FilePreviewSidebar } from '@/components/media/file-preview'
import { toast } from 'sonner'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'

const topNav = [
    { title: 'Anasayfa', href: '/', isActive: false },
]

const PAGE_SIZE = 15

export const Route = createFileRoute('/_authenticated/medya/')({
    component: MediaPage,
})

function MediaCardSkeleton() {
    return (
        <Card className="overflow-hidden border-2">
            <div className="aspect-square bg-muted/40">
                <Skeleton className="w-full h-full rounded-none" />
            </div>
            <CardContent className="p-2">
                <Skeleton className="h-3 w-3/4" />
            </CardContent>
        </Card>
    )
}

function MediaPage() {
    const { media, isLoading, uploadMedia, deleteMedia, isDeleting, isUploading } = useMedia()
    const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null)
    const [fileToDelete, setFileToDelete] = useState<MediaFile | null>(null)
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const gridEndRef = useRef<HTMLDivElement>(null)

    const visibleMedia = media.slice(0, visibleCount)
    const hasMore = visibleCount < media.length

    const handleLoadMore = useCallback(() => {
        setIsLoadingMore(true)
        setTimeout(() => {
            setVisibleCount(prev => prev + PAGE_SIZE)
            setIsLoadingMore(false)
            setTimeout(() => {
                gridEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }, 50)
        }, 600)
    }, [])

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        try {
            await uploadMedia(file)
            if (e.target) e.target.value = ''
        } catch (err) {
            console.error(err)
        }
    }

    const handleDelete = async () => {
        if (!fileToDelete) return
        try {
            await deleteMedia(fileToDelete.path)
            if (selectedFile?.path === fileToDelete.path) {
                setSelectedFile(null)
            }
            setFileToDelete(null)
        } catch (err) {
            console.error(err)
        }
    }

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    return (
        <>
            <Header>
                <TopNav links={topNav} />
                <div className="ml-auto flex items-center gap-2">
                    <SearchComponent />
                    <ThemeSwitch />
                    <ProfileDropdown />
                </div>
            </Header>

            <Main>
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Medya Kütüphanesi</h1>
                        <p className="text-sm text-muted-foreground">
                            Sitedeki tüm görselleri ve dosyaları yönetin
                            {!isLoading && <span className="ml-1">({media.length} dosya)</span>}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Input
                                type="file"
                                className="hidden"
                                id="media-upload"
                                onChange={handleUpload}
                                accept="image/*,video/*,application/pdf"
                            />
                            <Button asChild disabled={isUploading}>
                                <label htmlFor="media-upload" className="cursor-pointer">
                                    {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                                    Yeni Yükle
                                </label>
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                    {/* Main Area */}
                    <div className="flex-1 space-y-4">

                        {isLoading ? (
                            /* Initial loading: 15 skeleton cards */
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                                    <MediaCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : media.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed rounded-lg bg-muted/20">
                                <ImageIcon className="h-12 w-12 mb-4 text-muted-foreground/50" />
                                <p className="text-muted-foreground">Medya dosyası bulunamadı.</p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {visibleMedia.map((file) => (
                                        <Card
                                            key={file.path}
                                            className={`cursor-pointer group overflow-hidden border-2 transition-all ${selectedFile?.path === file.path ? 'border-primary ring-2 ring-primary ring-offset-1' : 'hover:border-primary/50'
                                                }`}
                                            onClick={() => setSelectedFile(file)}
                                        >
                                            <div className="aspect-square relative bg-muted/40">
                                                <FilePreview
                                                    filename={file.filename}
                                                    url={file.url}
                                                    mimetype={file.mimetype}
                                                />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedFile(file);
                                                    }}>
                                                        <Info className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="icon" variant="destructive" className="h-8 w-8" onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFileToDelete(file);
                                                    }}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <CardContent className="p-2 truncate text-[11px] font-medium bg-background">
                                                {file.originalName}
                                            </CardContent>
                                        </Card>
                                    ))}

                                    {/* Loading more skeletons appear in the grid */}
                                    {isLoadingMore && Array.from({ length: 10 }).map((_, i) => (
                                        <MediaCardSkeleton key={`skeleton-${i}`} />
                                    ))}
                                </div>
                                <div ref={gridEndRef} />

                                {/* Load More Button */}
                                {hasMore && !isLoadingMore && (
                                    <div className="flex justify-center pt-4">
                                        <Button
                                            variant="outline"
                                            onClick={handleLoadMore}
                                            className="gap-2"
                                        >
                                            <ChevronDown className="h-4 w-4" />
                                            Daha Fazla ({media.length - visibleCount} dosya kaldı)
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Desktop Details Sidebar */}
                    <div className="hidden md:block w-80 space-y-4">
                        <Card className="sticky top-20">
                            <CardContent className="p-4 space-y-6">
                                <div className="aspect-square rounded-md overflow-hidden bg-muted flex items-center justify-center border">
                                    {selectedFile ? (
                                        <FilePreviewSidebar
                                            filename={selectedFile.filename}
                                            url={selectedFile.url}
                                            mimetype={selectedFile.mimetype}
                                        />
                                    ) : (
                                        <div className="text-center p-4">
                                            <ImageIcon className="h-10 w-10 mx-auto mb-2 text-muted-foreground/20" />
                                            <p className="text-xs text-muted-foreground">Detaylar için bir dosya seçin</p>
                                        </div>
                                    )}
                                </div>

                                {selectedFile && (
                                    <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-200">
                                        <div>
                                            <h3 className="font-semibold text-sm line-clamp-2">{selectedFile.originalName}</h3>
                                            <Badge variant="outline" className="mt-1 text-[10px]">{selectedFile.mimetype}</Badge>
                                        </div>

                                        <div className="grid grid-cols-1 gap-3 text-xs">
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <HardDrive className="h-3.5 w-3.5" />
                                                <span>Boyut: {formatSize(selectedFile.size)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Calendar className="h-3.5 w-3.5" />
                                                <span>Yükleme: {formatDate(selectedFile.createdAt)}</span>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-[10px] text-muted-foreground uppercase">Dosya Yolu</Label>
                                            <div className="flex gap-1">
                                                <Input readOnly value={selectedFile.url} className="h-8 text-[10px] font-mono" />
                                                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => {
                                                    navigator.clipboard.writeText(selectedFile.url)
                                                    toast.success('URL kopyalandı')
                                                }}>
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="pt-2 flex gap-2">
                                            <Button variant="outline" className="flex-1 h-9 text-xs" asChild>
                                                <a href={selectedFile.url} target="_blank" rel="noreferrer">Görüntüle</a>
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                className="flex-1 h-9 text-xs"
                                                onClick={() => setFileToDelete(selectedFile)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Sil
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Mobile Bottom Bar - shows when file selected */}
                    {selectedFile && (
                        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background border-t shadow-lg p-3 animate-in slide-in-from-bottom-4 duration-200">
                            <div className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-md overflow-hidden border bg-muted shrink-0">
                                    <FilePreview
                                        filename={selectedFile.filename}
                                        url={selectedFile.url}
                                        mimetype={selectedFile.mimetype}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{selectedFile.originalName}</p>
                                    <p className="text-[10px] text-muted-foreground">{formatSize(selectedFile.size)} • {selectedFile.mimetype}</p>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <Button size="sm" variant="outline" className="h-8 text-xs" asChild>
                                        <a href={selectedFile.url} target="_blank" rel="noreferrer">
                                            <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        className="h-8 text-xs"
                                        onClick={() => setFileToDelete(selectedFile)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setSelectedFile(null)}
                                    >
                                        <span className="text-lg leading-none">×</span>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Delete Confirmation */}
                <AlertDialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Dosyayı silmek istediğinize emin misiniz?</AlertDialogTitle>
                            <AlertDialogDescription>
                                <strong>{fileToDelete?.originalName}</strong> dosyası sunucudan kalıcı olarak silinecektir.
                                Bu dosyayı kullanan sayfalarda görseller bozulabilir. Bu işlem geri alınamaz.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>İptal</AlertDialogCancel>
                            <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                                Dosyayı Sil
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </Main>
        </>
    )
}
